import { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const WEB3FORMS_KEY = "5fcd23fe-7637-4890-b879-d07bbc2b881f";

export function ContactPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    description: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("access_key", WEB3FORMS_KEY);
      fd.append("subject", "New Contact Request - BugBuddies");
      fd.append("from_name", formData.fullName);
      fd.append("Full Name", formData.fullName);
      fd.append("Email", formData.email);
      fd.append("Phone", formData.phone || "");
      fd.append("Description", formData.description);

      const resp = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: fd,
      });

      const result = await resp.json();
      if (result.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            fullName: "",
            email: "",
            phone: "",
            description: "",
          });
        }, 3000);
      } else {
        console.error("Web3Forms error:", result);
        alert("Sorry, we couldn’t send your message. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      details: ["(306) 201-7883", "(306) 537-1423"], // Array for multiple lines
      subtitle: "Call for immediate assistance",
    },
    {
      icon: Mail,
      title: "Email",
      details: "info@bugbuddies.ca",
      subtitle: "We respond within 24 hours",
    },
    {
      icon: MapPin,
      title: "Service Area",
      details: "Metro Area & Suburbs",
      subtitle: "All over Saskatchewan",
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "Mon-Fri: 8AM - 6PM",
      subtitle: "Sat-Sun: By Appointment",
    },
  ];

  if (isSubmitted) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Helmet>
            <title>Contact BugBuddies Pest Solutions</title>
            <meta name="description" content="Thank you for reaching out to BugBuddies. We'll respond to your pest control inquiry soon." />
            <link rel="canonical" href="https://bugbuddies.ca/contact" />
          </Helmet>
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="h-16 w-16 text-[#075123] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
              <p className="text-gray-600 mb-4">
                Your request has been submitted successfully.
                We&apos;ll contact you within 24 hours to schedule your free inspection.
              </p>
              <p className="text-sm text-gray-500">Redirecting to homepage...</p>
            </CardContent>
          </Card>
        </div>
    );
  }

  return (
      <div className="min-h-screen">
        <Helmet>
          <title>Contact BugBuddies Pest Solutions</title>
          <meta name="description" content="Contact BugBuddies for professional pest control services and free visual inspections." />
          <link rel="canonical" href="https://bugbuddies.ca/contact" />
        </Helmet>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#075123] to-[#05401A] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contact BugBuddies
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Ready to get rid of your pest problem? Contact us today for a free inspection and personalized quote.
            </p>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Request Free Visual Inspection
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Fill out the form below and we&apos;ll contact you to schedule your free inspection.
              </p>
            </div>

            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field */}
                  <input
                      type="checkbox"
                      name="botcheck"
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                          id="fullName"
                          name="fullName"
                          type="text"
                          required
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description of Pest Problem *</Label>
                    <Textarea
                        id="description"
                        name="description"
                        required
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Please describe your pest problem..."
                        rows={4}
                        className="mt-1"
                    />
                  </div>

                  <Button
                      type="submit"
                      className="w-full bg-[#075123] hover:bg-[#05401A] text-white disabled:opacity-60"
                      size="lg"
                      disabled={isSubmitting}
                  >
                    {isSubmitting
                        ? "Submitting..."
                        : "Submit Request for Free Visual Inspection"}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    * Required fields. We&apos;ll contact you within 24 hours during business hours.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactInfo.map((info, index) => {
                const isClickable = info.title === "Phone" || info.title === "Email";
                const href =
                    info.title === "Phone"
                        ? `tel:${
                            Array.isArray(info.details)
                                ? info.details[0].replace(/\D/g, "")
                                : info.details.replace(/\D/g, "")
                        }`
                        : info.title === "Email"
                            ? `mailto:${info.details}`
                            : undefined;


                const cardContent = (
                    <Card
                        className={`text-center p-6 transition hover:shadow-lg ${
                            isClickable ? "cursor-pointer" : ""
                        }`}
                    >
                      <CardContent className="pt-6">
                        <info.icon className="h-10 w-10 text-[#075123] mx-auto mb-4" />
                        <h3 className="font-semibold text-gray-900 mb-2">{info.title}</h3>
                        <p className="text-gray-900 mb-1">
                          {Array.isArray(info.details)
                              ? info.details.map((line, idx) => (
                                  <span key={idx}>
                              {line} <br />
                            </span>
                              ))
                              : info.details}
                        </p>
                        <p className="text-sm text-gray-600">{info.subtitle}</p>
                      </CardContent>
                    </Card>
                );

                return isClickable ? (
                    <a href={href} key={index} target="_blank" rel="noopener noreferrer">
                      {cardContent}
                    </a>
                ) : (
                    <div key={index}>{cardContent}</div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
  );
}
